import {
  BlockType,
  defaultFilterConfig,
  FilterQueryParser,
  type IndexedAnyBlock,
  isIndexedAnyBlock,
} from '@github-ui/filter-core'
import {MAX_QUERY_LENGTH} from '@github-ui/filter-query'

export const ACTIVE_ISSUE_FILTER = '(category:planned OR category:started)'
export type IssueStateFilter = 'open' | 'active' | 'closed'

type IssueStateQueryAnalysis = {kind: 'none'} | {kind: 'selected'; state: IssueStateFilter} | {kind: 'ambiguous'}

type QueryNode = (
  | {kind: 'term'; value: string}
  | {kind: 'and' | 'or'; children: QueryNode[]}
  | {kind: 'group'; child: QueryNode}
) & {start: number; end: number}

const parser = new FilterQueryParser([], {...defaultFilterConfig, groupAndKeywordSupport: true})

function combine(kind: 'and' | 'or', children: QueryNode[]): QueryNode | undefined {
  const first = children[0]
  const last = children.at(-1)
  if (!first || !last) return undefined
  return children.length === 1 ? first : {kind, children, start: first.start, end: last.end}
}

function parseBlocks(blocks: IndexedAnyBlock[], query: string): QueryNode | undefined {
  const alternatives: QueryNode[] = []
  let conjunction: QueryNode[] = []
  let expectsOperand = true
  let previousEnd: number | undefined

  for (const block of blocks) {
    if (block.type === BlockType.Space) continue
    if ('valid' in block && block.valid === false) return undefined
    if (previousEnd === block.startIndex) return undefined
    const range = {start: block.startIndex, end: block.startIndex + block.raw.length}
    previousEnd = range.end
    const value = query.slice(range.start, range.end)
    if (value === 'AND' || value === 'OR') {
      if (expectsOperand) return undefined
      if (value === 'OR') {
        const branch = combine('and', conjunction)
        if (!branch) return undefined
        alternatives.push(branch)
        conjunction = []
      }
      expectsOperand = true
    } else {
      let node: QueryNode
      if (block.type === BlockType.Group) {
        const child = parseBlocks(block.blocks, query)
        if (!child) return undefined
        node = {kind: 'group', child, ...range}
      } else {
        node = {kind: 'term', value, ...range}
      }
      conjunction.push(node)
      expectsOperand = false
    }
  }
  if (expectsOperand) return undefined
  const branch = combine('and', conjunction)
  if (!branch) return undefined
  alternatives.push(branch)
  return combine('or', alternatives)
}

function maskQuotedText(query: string): string | undefined {
  const masked = query.split('')
  let quote: string | undefined
  let termStart = true
  let filterValue = false

  // Preserve offsets while shielding both backend quote styles from filter-core's double-quote tokenizer.
  for (let index = 0; index < query.length; index++) {
    const character = query.charAt(index)
    if (quote) {
      if (character === '\\' && (query.charAt(index + 1) === quote || query.charAt(index + 1) === '\\')) {
        masked[index] = 'x'
        masked[++index] = 'x'
      } else if (character === quote) {
        masked[index] = '"'
        quote = undefined
      } else {
        masked[index] = 'x'
      }
    } else if (character === '"' || (character === "'" && (termStart || filterValue))) {
      quote = character
      masked[index] = '"'
      termStart = false
    } else if (/\s|[()]/.test(character)) {
      termStart = true
      filterValue = false
    } else {
      if (character === ':') filterValue = true
      termStart = false
    }
  }
  return quote ? undefined : masked.join('')
}

function parseQuery(query: string): QueryNode | undefined {
  if (query.length > MAX_QUERY_LENGTH) return undefined
  const masked = maskQuotedText(query)
  if (masked === undefined) return undefined
  const blocks = parser.parse(masked).blocks
  return blocks.every(isIndexedAnyBlock) ? parseBlocks(blocks, query) : undefined
}

export function isActiveIssueFilterAvailable(
  options: {statuses?: readonly string[] | null; categories?: readonly string[] | null} | null | undefined,
): boolean {
  return Boolean(
    options?.statuses?.length && options.categories?.includes('planned') && options.categories.includes('started'),
  )
}

function isActiveFilter(node: QueryNode): boolean {
  if (node.kind === 'group') return isActiveFilter(node.child)
  if (node.kind !== 'or') return false

  const categories = new Set<string>()
  function visit(child: QueryNode): boolean {
    if (child.kind === 'group') return visit(child.child)
    if (child.kind === 'or') return child.children.every(visit)
    if (child.kind !== 'term') return false

    const match = /^category:(?:(["'])([^"']+)\1|([^"']+))$/.exec(child.value)
    const category = (match?.[2] ?? match?.[3])?.toLowerCase()
    if (category !== 'planned' && category !== 'started') return false
    categories.add(category)
    return true
  }

  return visit(node) && categories.size === 2
}

function analyzeNode(node: QueryNode): IssueStateQueryAnalysis {
  if (isActiveFilter(node)) return {kind: 'selected', state: 'active'}
  if (node.kind === 'group') return analyzeNode(node.child)
  if (node.kind === 'term') {
    const match = /^(?:state|is):(?:(["'])([^"']+)\1|([^"']+))$/.exec(node.value)
    const state = (match?.[2] ?? match?.[3])?.toLowerCase()
    if (state === 'open' || state === 'closed') return {kind: 'selected', state}
    return /^-?state:/i.test(node.value) || /^-is:(?:(["'])(?:open|closed)\1?|open|closed)$/i.test(node.value)
      ? {kind: 'ambiguous'}
      : {kind: 'none'}
  }
  const analyses = node.children.map(analyzeNode)
  if (analyses.some(analysis => analysis.kind === 'ambiguous')) return {kind: 'ambiguous'}
  const states = analyses.flatMap(analysis => (analysis.kind === 'selected' ? [analysis.state] : []))
  if (states.length === 0) return {kind: 'none'}
  if (node.kind === 'or' && states.length !== analyses.length) return {kind: 'ambiguous'}

  if (states.includes('closed')) {
    return states.every(state => state === 'closed') ? {kind: 'selected', state: 'closed'} : {kind: 'ambiguous'}
  }

  if (node.kind === 'and') return {kind: 'selected', state: states.includes('active') ? 'active' : 'open'}
  // Mixed Active and Open branches have no single shortcut: either link would change one branch.
  if (states.every(state => state === 'active')) return {kind: 'selected', state: 'active'}
  return states.every(state => state === 'open') ? {kind: 'selected', state: 'open'} : {kind: 'ambiguous'}
}

export function analyzeIssueStateQuery(query: string | undefined): IssueStateQueryAnalysis {
  if (!query?.trim()) return {kind: 'none'}

  const node = parseQuery(query)
  return node ? analyzeNode(node) : {kind: 'ambiguous'}
}

export function getIssueStateFromQuery(query: string | undefined): IssueStateFilter | undefined {
  const analysis = analyzeIssueStateQuery(query)
  return analysis.kind === 'selected' ? analysis.state : undefined
}

function canMatchIssues(node: QueryNode): boolean {
  if (node.kind === 'group') return canMatchIssues(node.child)
  if (node.kind !== 'term') {
    return node.kind === 'and' ? node.children.every(canMatchIssues) : node.children.some(canMatchIssues)
  }

  const match = /^(-?)(?:is|type):(?:(["'])(issue|issues|pr|pull-request)\2|(issue|issues|pr|pull-request))$/.exec(
    node.value,
  )
  if (!match) return true
  const value = match[3] ?? match[4]
  const issues = value === 'issue' || value === 'issues'
  return match[1] ? !issues : issues
}

function buildIssueStateQuery(query: string | undefined, state: IssueStateFilter): string | undefined {
  const predicate = state === 'active' ? ACTIVE_ISSUE_FILTER : `state:${state}`
  if (!query?.trim()) return predicate

  const root = parseQuery(query)
  if (!root || analyzeNode(root).kind === 'ambiguous') return undefined
  if (state === 'active' && !canMatchIssues(root)) return undefined

  const replacements: QueryNode[] = []
  function visit(node: QueryNode) {
    // Only the complete Active union is shortcut-owned; individual category filters stay intact.
    if (isActiveFilter(node) || (node.kind === 'term' && analyzeNode(node).kind === 'selected')) {
      replacements.push(node)
    } else if (node.kind === 'group') {
      visit(node.child)
    } else if (node.kind === 'and' || node.kind === 'or') {
      for (const child of node.children) visit(child)
    }
  }
  visit(root)
  let result = ''
  if (replacements.length === 0) {
    result = `${root.kind === 'or' ? `(${query})` : query} AND ${predicate}`
  } else {
    let previousEnd = 0
    for (const node of replacements) {
      result += query.slice(previousEnd, node.start) + predicate
      previousEnd = node.end
    }
    result += query.slice(previousEnd)
    if (replacements.length > 1) result = withoutRepeatedPredicate(result, predicate)
  }
  return parseQuery(result) ? result : undefined
}

// Replacing both `state:open` and the Active clause in one conjunction would repeat the predicate.
function withoutRepeatedPredicate(query: string, predicate: string): string {
  const root = parseQuery(query)
  if (!root) return query

  const removals: Array<[number, number]> = []
  function visit(node: QueryNode) {
    if (node.kind === 'group') return visit(node.child)
    if (node.kind === 'term') return
    let seen = false
    for (const [index, child] of node.children.entries()) {
      const previous = node.children[index - 1]
      if (node.kind !== 'and' || query.slice(child.start, child.end) !== predicate) {
        visit(child)
      } else if (seen && previous) {
        removals.push([previous.end, child.end])
      } else {
        seen = true
      }
    }
  }
  visit(root)

  let result = ''
  let previousEnd = 0
  for (const [start, end] of removals) {
    result += query.slice(previousEnd, start)
    previousEnd = end
  }
  return result + query.slice(previousEnd)
}

export function addIssueStateToQuery(query: string | undefined, state: IssueStateFilter): string {
  const result = buildIssueStateQuery(query, state)
  if (result === undefined) throw new Error('Cannot apply an issue quick filter to an invalid or too complex query.')
  return result
}

// A blank query has no scope to keep, so it gets no shortcut, like Open and Closed.
export function getActiveIssueFilterQuery(query: string | undefined): string | undefined {
  return query?.trim() ? buildIssueStateQuery(query, 'active') : undefined
}

/**
 * A selected Active filter is part of the list query, so the list search can't count Open and
 * Closed. Counting the Open link's query instead returns all three counts. Pull-request-only
 * queries get no Active shortcut, so they keep counting their own query.
 */
export function getIssueStateCountsQuery(query: string | undefined): string | undefined {
  if (getIssueStateFromQuery(query) !== 'active' || getActiveIssueFilterQuery(query) === undefined) return undefined
  return buildIssueStateQuery(query, 'open')
}

type IssueStateCounts = {openCount?: number | null; activeCount?: number | null; closedCount?: number | null}
type SelectedIssueStateCounts = {openCount?: number; activeCount?: number; closedCount?: number}

/**
 * Picks the Open, Active and Closed counts to show. While the offered Active shortcut is selected, only the
 * deferred counts of the Open link's query describe all three. Where Active isn't offered, those counts describe a
 * broader query than the links, so the list's own counts are kept.
 */
export function selectIssueStateCounts(
  query: string | undefined,
  activeOffered: boolean,
  {list, deferred}: {list?: IssueStateCounts | null; deferred?: IssueStateCounts | null},
): SelectedIssueStateCounts {
  const countsOpenLink = getIssueStateCountsQuery(query) !== undefined
  const listCounts = countsOpenLink && activeOffered ? undefined : list
  const deferredCounts = countsOpenLink && !activeOffered ? undefined : deferred
  return {
    openCount: deferredCounts?.openCount ?? listCounts?.openCount ?? undefined,
    activeCount: deferredCounts?.activeCount ?? listCounts?.activeCount ?? undefined,
    closedCount: deferredCounts?.closedCount ?? listCounts?.closedCount ?? undefined,
  }
}
